/**
 * IntentionsSection Component
 * Displays user's selected cultural intentions/values in the My Rituality dashboard.
 * Shows intentions in a responsive card grid layout with visual indication for selected ones.
 */

import { ArrowRight, Plus } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { CustomInput } from "@/components/forms/customInput"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useIntentions } from "@/features/profile/hooks/useIntentions"
import { useTranslation } from "@/language"

export function IntentionsSection() {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const { intentions, isLoading, toggleIntention, isToggling, createCustomIntention, isCreating } =
    useIntentions(user?.id ?? "")

  // State for custom intention input
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customIntentionName, setCustomIntentionName] = useState("")
  // State to hide section after assigning first intention (only happens once)
  const [isVisible, setIsVisible] = useState(() => {
    // Check if user has already assigned first intention in the past
    const hasAssignedFirst = localStorage.getItem(`intentions_first_assigned_${user?.id}`)
    return !hasAssignedFirst // hide only if they haven't assigned before
  })
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previousCheckedCountRef = useRef(0)

  // Count checked intentions
  const checkedIntentionsCount = intentions.filter(i => i.is_checked).length

  // Handle auto-hide after first intention is assigned (only happens once per user)
  useEffect(() => {
    // Check if user has already gone through first assignment flow
    const hasAssignedFirst = localStorage.getItem(`intentions_first_assigned_${user?.id}`)

    // If user had 0 checked intentions and now has >0, and this is their first time
    if (!hasAssignedFirst && previousCheckedCountRef.current === 0 && checkedIntentionsCount > 0) {
      // Mark that user has assigned their first intention
      localStorage.setItem(`intentions_first_assigned_${user?.id}`, "true")

      // Start 3 second timer to hide section
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false)
      }, 3000) // 3 seconds
    }
    // Update previous count for next comparison
    previousCheckedCountRef.current = checkedIntentionsCount

    // Cleanup timeout on unmount
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [checkedIntentionsCount, user?.id])

  // Handle intention card click
  const handleIntentionClick = (intentionId: string) => {
    toggleIntention(intentionId)
  }

  // Handle adding custom intention
  const handleAddCustomIntention = () => {
    if (!customIntentionName.trim()) {
      toast.error("Please enter an intention name")
      return
    }

    createCustomIntention(
      {
        name: customIntentionName.trim(),
        description: customIntentionName.trim(),
      },
      {
        onSuccess: () => {
          toast.success("Custom intention added")
          setCustomIntentionName("")
          setShowCustomInput(false)
        },
      },
    )
  }

  if (isLoading) {
    return (
      <section>
        <div className="h-6 w-40 bg-white/5 rounded animate-pulse mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 bg-white/5 rounded-[20px] animate-pulse" />
          ))}
        </div>
      </section>
    )
  }

  // Hide section after user assigns first intention
  if (!isVisible) {
    return null
  }

  return (
    <section>
      {/* Section heading */}
      <div className="flex items-center gap-3.75">
        <h2 className="text-2xl font-semibold leading-[30px] uppercase opacity-90 text-primary">
          {t("intentions.title")}
        </h2>
        <div className="flex items-center gap-2">
          {intentions.length > 0 && (
            <button
              type="button"
              onClick={() => navigate("/profile/edit#tab=intentions")}
              className="text-sm text-primary/70 hover:text-primary transition-colors flex items-center gap-1.25 cursor-pointer"
            >
              {t("intentions.edit")}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {/* Description */}
      <p className="text-sm text-primary leading-[25px] mt-2.5">{t("intentions.subtitle")}</p>

      {/* Intentions grid */}
      {intentions.length > 0 ? (
        <div className="flex flex-wrap gap-2.5 mt-7.5">
          {intentions.map(intention => (
            <button
              key={intention.id}
              type="button"
              onClick={() => handleIntentionClick(intention.id)}
              disabled={isToggling}
              className={`flex items-center rounded-full gap-1.25 px-3.75 py-2.5 h-[50px] transition-all ${
                intention.is_checked
                  ? "bg-[#5A5B3F] border-2 border-[#5A5B3F]"
                  : "bg-transparent border border-[#F2F0EB]"
              } ${isToggling ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary/50"}`}
            >
              {/* Selected indicator */}
              {!intention.is_checked && <Plus className="w-4 h-4 text-primary" />}
              <h3 className="text-[13px] leading-5 font-semibold text-[#F2F0EB]">
                {intention.title}
              </h3>
            </button>
          ))}

          {/* Add Custom Intention Button */}
          {!showCustomInput && (
            <button
              type="button"
              onClick={() => setShowCustomInput(!showCustomInput)}
              className="text-sm text-primary/70 hover:text-primary transition-colors flex items-center gap-1.25"
            >
              <Plus className="w-4 h-4" />
              {t("intentions.addCustom")}
            </button>
          )}

          {/* Custom Intention Input */}
          {showCustomInput && (
            <div className="mt-4 w-full max-w-md">
              <div className="flex gap-2">
                <CustomInput
                  type="text"
                  value={customIntentionName}
                  onChange={e => setCustomIntentionName(e.target.value)}
                  placeholder={t("intentions.customPlaceholder")}
                  disabled={isCreating}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      handleAddCustomIntention()
                    } else if (e.key === "Escape") {
                      setShowCustomInput(false)
                      setCustomIntentionName("")
                    }
                  }}
                  label=""
                  autoFocus
                  className="text-[#F2F0EB] border rounded-lg p-1 pl-3 h-[40px]!"
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddCustomIntention}
                  disabled={isCreating || !customIntentionName.trim()}
                  isLoading={isCreating}
                >
                  {t("intentions.add")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCustomInput(!showCustomInput)}
                >
                  {t("intentions.cancel")}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => navigate("/edit-profile?section=intentions")}
            className="w-full bg-white/5 border border-white/15 hover:border-primary/30 rounded-[15px] p-6 text-left transition-all group"
          >
            <p className="text-sm text-primary/70 group-hover:text-primary transition-colors">
              {t("intentions.addIntentions")}
            </p>
          </button>
        </div>
      )}
    </section>
  )
}
